import React, { useState } from 'react';
import { Pressable, View } from 'react-native';

import Text from '../../../components/text/Text';
import IconAction from '../../../design/components/IconAction';
import PrimaryButton from '../../../design/components/PrimaryButton';
import SectionTitle from '../../../design/components/SectionTitle';
import Sheet from '../../../design/components/Sheet';
import TickRow from '../../../design/components/TickRow';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

// Long tool lists get the tall sheet (same threshold as the roster option sheets).
const FULL_HEIGHT_TOOL_COUNT = 8;
// Gives the Edit link a 44-high touch area without making the title row taller
// or moving the word off the right edge.
const EDIT_HIT_SLOP = { top: 12, bottom: 12, left: 12, right: 12 };

/**
 * "Tools" on Home: the shown tools, two tiles per row, and an Edit link that
 * opens a sheet to tick which tools show.
 * - tools: every tool the user can open, in Home order:
 *   [{ key, label, icon, badge, onPress, isShown }]
 * - onToggleTool(key, isShown), onDoneEditing() (the sheet closed)
 */
const ToolsGrid = ({ tools, onToggleTool, onDoneEditing }) => {
	const styles = useThemedStyles(createStyles);
	const [isEditing, setIsEditing] = useState(false);
	const tiles = tools.filter(tool => tool.isShown);

	const closeEditor = () => {
		setIsEditing(false);
		onDoneEditing();
	};

	const rows = [];
	for (let index = 0; index < tiles.length; index += 2) {
		rows.push(tiles.slice(index, index + 2));
	}

	return (
		<View style={styles.section}>
			<View style={styles.header}>
				<SectionTitle title="Tools" style={styles.title} />
				<Pressable
					onPress={() => setIsEditing(true)}
					accessibilityRole="button"
					accessibilityLabel="Edit tools"
					hitSlop={EDIT_HIT_SLOP}
					style={({ pressed }) => pressed && styles.pressed}
				>
					<Text style={styles.editText}>Edit</Text>
				</Pressable>
			</View>
			{!tiles.length && <Text style={styles.hint}>No tools on Home. Tap Edit to add some.</Text>}
			{rows.map(row => (
				<View key={row[0].key} style={styles.row}>
					{row.map(tile => (
						<View key={tile.key} style={styles.cell}>
							<IconAction
								variant="tile"
								icon={tile.icon}
								label={tile.label}
								badge={tile.badge}
								onPress={tile.onPress}
								style={styles.tile}
							/>
						</View>
					))}
					{row.length === 1 && <View style={styles.cell} />}
				</View>
			))}
			<Sheet
				isVisible={isEditing}
				onClose={closeEditor}
				title="Tools on Home"
				fullHeight={tools.length > FULL_HEIGHT_TOOL_COUNT}
				footer={<PrimaryButton label="Done" onPress={closeEditor} />}
			>
				{tools.map(tool => (
					<TickRow
						key={tool.key}
						title={tool.label}
						checked={tool.isShown}
						quietWhenChecked={false}
						onToggle={isShown => onToggleTool(tool.key, isShown)}
					/>
				))}
			</Sheet>
		</View>
	);
};

const createStyles = ({ ui: { colors, space, type } }) => ({
	section: {
		gap: space.sm,
		marginTop: space.xs,
	},
	header: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
	},
	title: {
		flex: 1,
	},
	editText: {
		...type.bodySmallStrong,
		color: colors.accent,
	},
	pressed: {
		opacity: 0.7,
	},
	hint: {
		...type.secondary,
		color: colors.textSecondary,
	},
	row: {
		flexDirection: 'row',
		gap: space.sm,
	},
	// The cell, not the padded tile, takes the column width so a lone last tile matches the tiles above it.
	cell: {
		flex: 1,
	},
	tile: {
		flex: 1,
	},
});

export default ToolsGrid;
